/**
 * Battlefield shop pads — fly close and dock (E / T) to trade, refuel, or buy weapons.
 * Sprites live in public/assets/shops/{market,fuel,armory}.png
 */

const BASE = (typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL) || './';

function asset(path) {
    const b = BASE.endsWith('/') ? BASE : BASE + '/';
    const p = path.startsWith('/') ? path.slice(1) : path;
    return b + p;
}

export const SHOP_KINDS = {
    market: {
        id: 'market',
        label: 'Market',
        verb: 'Trade',
        accent: '#44ddff',
        names: ['Night Bazaar', 'Dust Exchange', 'Freeport Stall', 'Cargo Pit', 'Relay Mart'],
        src: asset('assets/shops/market.png'),
    },
    fuel: {
        id: 'fuel',
        label: 'Fuel Depot',
        verb: 'Refuel',
        accent: '#ffb040',
        names: ['Pump 19', 'Deuterium Well', 'Tank Farm', 'Ion Hose', 'Warp Tap'],
        src: asset('assets/shops/fuel.png'),
    },
    armory: {
        id: 'armory',
        label: 'Armory',
        verb: 'Arm',
        accent: '#ff66cc',
        names: ['Gun Rack', 'Vault Arms', 'Black Iron', 'Ordnance Pad', 'Spike Locker'],
        src: asset('assets/shops/armory.png'),
    },
};

const images = {};
let loadPromise = null;

function loadImg(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('fail ' + src));
        img.src = src;
    });
}

export function loadShopSprites() {
    if (loadPromise) return loadPromise;
    loadPromise = Promise.all(
        Object.values(SHOP_KINDS).map((k) =>
            loadImg(k.src).then((img) => { images[k.id] = img; }).catch(() => {})
        )
    ).catch((err) => console.warn('[shops] load failed', err));
    return loadPromise;
}

export function getShopSprite(kind) {
    return images[kind] || null;
}

function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

export class WorldShop {
    /**
     * @param {number} x
     * @param {number} y
     * @param {'market'|'fuel'|'armory'} kind
     * @param {{ stock?: object[], name?: string, planetName?: string }=} opts
     */
    constructor(x, y, kind, opts = {}) {
        const meta = SHOP_KINDS[kind] || SHOP_KINDS.market;
        this.x = x;
        this.y = y;
        this.kind = meta.id;
        this.label = meta.label;
        this.verb = meta.verb;
        this.accent = meta.accent;
        this.name = opts.name || pick(meta.names);
        this.planetName = opts.planetName || '';
        this.stock = opts.stock || [];
        this.radius = 78;
        this.interactR = 108;
        this.pulse = Math.random() * Math.PI * 2;
        this.bob = Math.random() * Math.PI * 2;
        this.ring = Math.random() * Math.PI * 2;
    }

    update() {
        this.pulse += 0.045;
        this.bob += 0.028;
        this.ring += 0.012;
    }

    inRange(player, extra = 0) {
        if (!player) return false;
        const d = Math.hypot(this.x - player.x, this.y - player.y);
        return d < this.interactR + extra;
    }

    distanceTo(player) {
        if (!player) return Infinity;
        return Math.hypot(this.x - player.x, this.y - player.y);
    }

    draw(ctx, opts = {}) {
        const nearby = !!opts.nearby;
        const bob = Math.sin(this.bob) * 2.4;
        const pulse = 0.55 + Math.sin(this.pulse) * 0.2;
        const size = 132;
        const y = this.y + bob;
        const padR = this.radius + (nearby ? 10 : 4);

        ctx.save();

        // Soft circular pad (never a square)
        const bloom = ctx.createRadialGradient(this.x, y, 10, this.x, y, padR + 28);
        bloom.addColorStop(0, this._rgba(this.accent, 0.28 * pulse));
        bloom.addColorStop(0.5, this._rgba(this.accent, 0.08));
        bloom.addColorStop(1, 'transparent');
        ctx.fillStyle = bloom;
        ctx.beginPath();
        ctx.arc(this.x, y, padR + 28, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = 'rgba(6, 10, 22, 0.55)';
        ctx.beginPath();
        ctx.arc(this.x, y, padR - 6, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = this._rgba(this.accent, nearby ? 0.9 : 0.45);
        ctx.lineWidth = nearby ? 2.2 : 1.3;
        ctx.setLineDash([7, 9]);
        ctx.lineDashOffset = -this.ring * 22;
        ctx.beginPath();
        ctx.arc(this.x, y, padR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        const img = images[this.kind];
        if (img && img.complete && img.naturalWidth) {
            const aspect = img.naturalWidth / img.naturalHeight;
            let dw = size;
            let dh = size;
            if (aspect > 1.05) dh = size / aspect;
            else if (aspect < 0.95) dw = size * aspect;
            // Circular clip so any leftover square fringe never shows
            ctx.save();
            ctx.beginPath();
            ctx.arc(this.x, y, padR + 2, 0, Math.PI * 2);
            ctx.clip();
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, this.x - dw / 2, y - dh / 2, dw, dh);
            ctx.restore();
        } else {
            this._drawFallback(ctx, y, size);
        }

        ctx.font = '700 11px Orbitron, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = this.accent;
        ctx.shadowColor = this.accent;
        ctx.shadowBlur = 8;
        ctx.fillText(this.name.toUpperCase(), this.x, y - padR - 10);
        ctx.shadowBlur = 0;
        ctx.font = '600 10px Rajdhani, sans-serif';
        ctx.fillStyle = 'rgba(200,230,255,0.75)';
        ctx.fillText(this.label, this.x, y - padR + 4);

        if (nearby) {
            ctx.font = '700 13px Orbitron, sans-serif';
            ctx.fillStyle = '#fff8e0';
            ctx.shadowColor = this.accent;
            ctx.shadowBlur = 10;
            ctx.fillText(`E · ${this.verb.toUpperCase()}`, this.x, y + padR + 16);
            ctx.shadowBlur = 0;
        }

        ctx.restore();
    }

    _drawFallback(ctx, y, size) {
        const r = size * 0.32;
        ctx.fillStyle = 'rgba(8,12,28,0.85)';
        ctx.beginPath();
        ctx.arc(this.x, y, r + 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = this.accent;
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = this.accent;
        ctx.font = '700 18px Orbitron, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const glyph = this.kind === 'fuel' ? '⛽' : this.kind === 'armory' ? '⚔' : '✚';
        ctx.fillText(glyph, this.x, y);
    }

    _rgba(hex, a) {
        const c = (hex || '#ffffff').replace('#', '');
        if (c.length !== 6) return hex;
        const r = parseInt(c.slice(0, 2), 16);
        const g = parseInt(c.slice(2, 4), 16);
        const b = parseInt(c.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${a})`;
    }
}
